import { WS_URL } from "@/lib/api";
import { WebsocketService } from "@/services/websocketService";
import { forceFullLogout } from "@/lib/forceLogout";
import { getAccessToken } from "@/lib/tokenSession";

const RECONNECT_DELAY = 3000;
const MAX_RECONNECT_ATTEMPTS = 5;
const MAX_RECONNECT_DELAY = 30000;
// The server accepts the socket, sends `connection_failed`, then closes with
// this code when the credential is rejected. Because it accepts first, the
// browser fires `onopen` — so a naive "reset the counter on open" would retry
// a refused credential forever at RECONNECT_DELAY.
const WS_UNAUTHORIZED = 4001;
// The session behind the socket ended — revoked, deactivated, or a role
// change. Reconnecting into it is a refusal loop, so sign out instead.
const WS_SESSION_ENDED = 4002;
// The server stopped hearing pongs and gave up on this socket. Named rather
// than left to the default branch because the close-code set is one truth
// with two readers (`server/app/api/v2/websocket/router.py` declares it):
// this one needs no branch of its own — a stale socket reconnects, which is
// what every code but 4001 and 4002 already does.
const WS_STALE = 4003;
// A socket has to survive this long before we call the connection healthy and
// reset the backoff, for the same reason.
const HEALTHY_AFTER_MS = 5000;
// The server pings every 20 seconds. Two intervals plus a margin of silence
// means the socket is gone in a way the browser has not noticed — a sleeping
// laptop, a dropped NAT entry — so rebuild it rather than wait forever.
const SILENCE_LIMIT_MS = 45000;
// ±25 % on every backoff step. A deploy drops every socket at once, and
// without jitter the whole fleet retries on the same tick.
const BACKOFF_JITTER = 0.25;
// `WS_MAX_SUBSCRIPTION_KEYS_PER_CONNECTION` in `server/app/core/config.py`.
// Refused here too, so the server's silent refusal is never the first sign.
const MAX_SUBSCRIPTION_KEYS = 200;

/**
 * The one socket. Screens never touch it: they go through
 * `useLiveResource`, which subscribes to resource keys and refetches.
 *
 * Time is injected so the backoff, the healthy timer and the jitter are
 * assertable without sleeping.
 */
export class WebSocketClient {
  constructor(deps = {}) {
    const g = typeof globalThis === "undefined" ? {} : globalThis;
    this._setTimeout = deps.setTimeout || g.setTimeout?.bind(g);
    this._clearTimeout = deps.clearTimeout || g.clearTimeout?.bind(g);
    this._now = deps.now || (() => Date.now());
    this._random = deps.random || (() => Math.random());

    this.socket = null;
    this.listeners = new Set();
    this.reconnectAttempts = 0;
    // A ticket can expire in flight, so one refusal earns a fresh ticket. Two
    // in a row is the server saying no, not a race.
    this.unauthorizedRetries = 0;
    this.shouldReconnect = true;
    this.reconnectTimer = null;
    this.healthyTimer = null;
    // Ref-counted: two hooks may both hold `job`, and one unmounting must not
    // unsubscribe the other.
    this.keyCounts = new Map();
    // The last seq seen per target on the *current* connection (§7.5). The
    // first value for a target is a baseline; the reconnect that preceded it
    // has already refetched.
    this.watermarks = new Map();
    this.silenceTimer = null;
    this._installRecoveryHooks();
  }

  /**
   * Nudge a reconnect when the browser comes back online or the tab becomes
   * visible again. Without this, a client that exhausted
   * MAX_RECONNECT_ATTEMPTS (e.g. every retry landed while the API server was
   * restarting) stays disconnected for the rest of the session and silently
   * stops receiving events — no spinner ever resolves.
   */
  _installRecoveryHooks() {
    if (typeof window === "undefined") return;

    const revive = () => {
      if (!this.shouldReconnect) return;
      if (this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING) return;
      this.reconnectAttempts = 0;
      this.connect();
    };

    window.addEventListener("online", revive);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") revive();
    });
  }

  /**
   * Mint a ticket and open the authenticated socket. The ticket is
   * single-use and lives only for the handshake, so a rotated access token no
   * longer strands a live socket the way `?access_token=` did.
   */
  async connect() {
    this.shouldReconnect = true;
    const wasPublic = this._transport?.transport === "public";
    this._transport = { transport: "auth" };

    const live = this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING;
    // Signing in from the career board: the live socket is the public one,
    // and it carries board events and no notifications. The "already live"
    // guard alone returned here, so the ticket was never minted and the tab
    // stayed on the public socket until it happened to drop (I1701 W21).
    if (live && wasPublic) this._closePublic();
    else if (live) return;

    // Single-flight, the shape `authService.refresh` already uses. The
    // "already live" guard above reads `this.socket`, which is still null for
    // every caller that arrived while the ticket mint was in flight — so
    // three `useLiveResource` hooks mounting in one commit opened three
    // sockets, two of them orphans nothing pinged.
    if (this._connecting) return this._connecting;
    this._connecting = this._connectAuth().finally(() => {
      this._connecting = null;
    });
    return this._connecting;
  }

  async _connectAuth() {
    this._clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;

    let ticket;
    try {
      ticket = await WebsocketService.mintTicket();
    } catch (err) {
      console.warn("[WS] Ticket mint failed:", err?.message || err);
      this._scheduleReconnect();
      return;
    }
    if (!ticket) {
      this._scheduleReconnect();
      return;
    }

    this._open(`${WS_URL}?ticket=${ticket}`);
  }

  /**
   * The public board's socket: no credential, a tenant slug. A signed-in
   * visitor reading the same board stays on the authenticated socket, which
   * carries everything the public one does and more.
   */
  async connectPublic(slug) {
    this.shouldReconnect = true;
    this._transport = { transport: "public", slug };

    const live = this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING;
    if (live) return;
    if (!slug) return;

    // One in-flight attempt per tab, whichever transport asked for it — a tab
    // holds one socket, so two callers racing here would build two.
    if (this._connecting) return this._connecting;
    this._connecting = this._connectPublic(slug).finally(() => {
      this._connecting = null;
    });
    return this._connecting;
  }

  async _connectPublic(slug) {
    this._clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;

    const publicUrl = WS_URL.replace(/\/ws\/events(?=$|\?)/, "/ws/public");
    this._open(`${publicUrl}?slug=${encodeURIComponent(slug)}`);
  }

  /**
   * The compliance portal's socket: a magic-link token, no session.
   *
   * The third transport, and it is `/ws/events` like the authenticated one
   * rather than `/ws/public` — the server mints a real ticket for it, of
   * `kind: "link"`, scoped to the one compliance candidate the token names.
   * What differs is where the ticket comes from: the portal token instead of
   * a JWT, because the visitor has no session to mint against.
   *
   * @param {string} token  the portal token from the URL
   */
  async connectLink(token) {
    this.shouldReconnect = true;
    this._transport = { transport: "link", token };

    const live = this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING;
    if (live) return;
    if (!token) return;

    if (this._connecting) return this._connecting;
    this._connecting = this._connectLink(token).finally(() => {
      this._connecting = null;
    });
    return this._connecting;
  }

  async _connectLink(token) {
    this._clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;

    let ticket;
    try {
      // Minted fresh on every attempt, including reconnects: a ticket is
      // single-use and expires in 30 seconds, so a cached one is a handshake
      // that fails the second time.
      ticket = await WebsocketService.mintPortalTicket(token);
    } catch (err) {
      console.warn("[WS] Portal ticket mint failed:", err?.message || err);
      this._scheduleReconnect();
      return;
    }
    if (!ticket) {
      this._scheduleReconnect();
      return;
    }

    this._open(`${WS_URL}?ticket=${ticket}`);
  }

  /**
   * The one entry point a hook calls. Which socket a screen gets is decided
   * here rather than in the screen: a board is public only for a visitor with
   * no session.
   */
  ensureConnected({ transport = "auth", slug, token } = {}) {
    // `link` does not check for a session: the compliance portal is reached
    // by magic link and the visitor never has one. A signed-in officer who
    // happens to open a portal link still gets this socket, because the page
    // they are on is the candidate's, and the candidate's events are what it
    // renders.
    if (transport === "link") return this.connectLink(token);
    if (transport === "public" && !getAccessToken()) return this.connectPublic(slug);
    return this.connect();
  }

  _reconnect() {
    const t = this._transport?.transport;
    if (t === "public") return this.connectPublic(this._transport.slug);
    if (t === "link") return this.connectLink(this._transport.token);
    return this.connect();
  }

  _open(url) {
    // Logout is the race this guards. `_connectAuth` awaits `mintTicket()`,
    // and a sign-out inside that await calls `disconnect()` — which sets
    // `shouldReconnect = false` and nulls the socket, but cannot cancel a
    // promise already in flight. Without this check the mint resolved
    // afterwards and opened an authenticated socket for a browser nobody is
    // signed in on, streaming the tenant's events until the server's 60s
    // revalidator happened to close it. Checked here rather than in
    // `_connectAuth` so every path into `_open` is covered.
    if (!this.shouldReconnect) return;

    this.socket = new WebSocket(url);

    this.socket.onopen = () => {
      this._clearTimeout(this.healthyTimer);
      this.healthyTimer = this._setTimeout(() => {
        this.reconnectAttempts = 0;
        this.unauthorizedRetries = 0;
      }, HEALTHY_AFTER_MS);
      this._resendSubscriptions();
      this._armSilenceTimer();
      this._emit({ event: "__open__" });
    };

    this.socket.onmessage = (event) => {
      try {
        this._receive(JSON.parse(event.data));
      } catch (err) {
        console.error("[WS] Parse error:", err);
      }
    };

    this.socket.onclose = (event) => {
      this._clearTimeout(this.healthyTimer);
      this.healthyTimer = null;
      this._clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
      this.watermarks.clear();
      this._emit({ event: "__close__" });

      if (event.code === WS_SESSION_ENDED) {
        this.shouldReconnect = false;
        console.warn("[WS] Session ended — signing out");
        forceFullLogout();
        return;
      }

      if (event.code === WS_STALE) {
        console.info("[WS] The server gave up on a quiet socket — reconnecting");
      }

      if (event.code === WS_UNAUTHORIZED) {
        if (this.unauthorizedRetries >= 1) {
          console.warn("[WS] Ticket refused twice — not reconnecting");
          return;
        }
        this.unauthorizedRetries++;
      }

      this._scheduleReconnect();
    };

    this.socket.onerror = (err) => {
      console.error("[WS] Error:", err);
    };
  }

  _receive(frame) {
    this._armSilenceTimer();

    if (frame?.type === "ping") {
      this._send({ type: "pong" });
    }

    // The server refused something this socket asked for — today only a
    // subscription over the key cap. It arrives as `type`, never `event`, so
    // it reaches no `useLiveResource` listener and cannot be mistaken for a
    // change to a record. Before this frame existed the refusal was a log
    // line on the server and nothing at all here, so a tab over the cap
    // believed it was subscribed and quietly received nothing (I1768 W7).
    if (frame?.type === "error") {
      console.warn(`[WS] Server refused a frame: ${frame.code}`, frame.message);
      return;
    }

    // Run before the event itself: a listener refetching on the gap wants the
    // one refetch, not one per event it can prove it missed.
    if (this._hasGap(frame?.seq)) this._emit({ event: "__resync__" });

    if (frame?.event) this._emit(frame);
  }

  /**
   * True when any target's counter jumped FORWARD by more than one, or moved
   * backwards. Backwards is a Redis eviction or a restart, and the client's
   * state is as suspect either way.
   *
   * Unchanged is the ordinary idle case, not a gap: the 20-second ping carries
   * each target's *current* value, so two pings with nothing happening between
   * them carry the same number. Demanding `last + 1` read every one of those as
   * a missed message and refetched every mounted surface every 20 seconds,
   * forever, on every tab of every tenant whose counter existed (I1701 E2).
   *
   * One verdict per frame, so a burst that gaps five targets costs one refetch.
   */
  _hasGap(seq) {
    if (!seq || typeof seq !== "object") return false;
    let gapped = false;
    for (const [target, value] of Object.entries(seq)) {
      const last = this.watermarks.get(target);
      if (last !== undefined && (value > last + 1 || value < last)) gapped = true;
      this.watermarks.set(target, value);
    }
    return gapped;
  }

  _armSilenceTimer() {
    this._clearTimeout(this.silenceTimer);
    this.silenceTimer = this._setTimeout(() => this._onSilence(), SILENCE_LIMIT_MS);
  }

  /**
   * Rebuild the socket by hand rather than waiting for `onclose`: a socket
   * this dead often never fires it.
   */
  _onSilence() {
    console.warn("[WS] No frame for 45s — rebuilding the socket");
    const stale = this.socket;
    if (stale) {
      stale.onopen = stale.onmessage = stale.onclose = stale.onerror = null;
      stale.close();
    }
    this.socket = null;
    this.watermarks.clear();
    this._emit({ event: "__close__" });
    this._scheduleReconnect();
  }

  _scheduleReconnect() {
    if (!this.shouldReconnect) return;
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      // Not permanent: the `online` / `visibilitychange` hooks reset the
      // counter and retry, so the client recovers instead of going dark.
      console.warn("[WS] Reconnect attempts exhausted — will retry when the tab refocuses or the network returns");
      return;
    }

    const step = Math.min(RECONNECT_DELAY * 2 ** this.reconnectAttempts, MAX_RECONNECT_DELAY);
    const delay = step * (1 + BACKOFF_JITTER * (2 * this._random() - 1));
    this.reconnectAttempts++;
    this._clearTimeout(this.reconnectTimer);
    this.reconnectTimer = this._setTimeout(() => this._reconnect(), delay);
  }

  disconnect() {
    this.shouldReconnect = false;
    this._clearTimeout(this.reconnectTimer);
    this._clearTimeout(this.healthyTimer);
    this.reconnectTimer = null;
    this.healthyTimer = null;
    this.reconnectAttempts = 0;
    // Dropped so the next `connect()` is not handed the signed-out session's
    // in-flight attempt as its own. The promise itself cannot be cancelled;
    // the `shouldReconnect` guard in `_open` is what stops it opening a
    // socket, and this stops a new sign-in from awaiting a dead mint.
    this._connecting = null;
    this._clearTimeout(this.silenceTimer);
    this.silenceTimer = null;
    this.watermarks.clear();
    // `keyCounts` deliberately survives. Closing the socket and forgetting who
    // wanted what are different acts: the `useLiveResource` hooks holding
    // these keys are still mounted and will never call `subscribe` again, so
    // clearing the map left `_resendSubscriptions` with nothing to resend and
    // the next socket subscribed to nothing at all.
    this.socket?.close();
    this.socket = null;
  }

  /**
   * Drop the public socket without letting it schedule a reconnect: the
   * caller is about to open the authenticated one in its place.
   */
  _closePublic() {
    const board = this.socket;
    board.onopen = board.onmessage = board.onclose = board.onerror = null;
    board.close();
    this.socket = null;
    this._clearTimeout(this.silenceTimer);
    this.silenceTimer = null;
    this.watermarks.clear();
    this._emit({ event: "__close__" });
  }

  /**
   * Close the socket only if it is the authenticated one.
   *
   * `useWsConnection` sits above every page and owns the authenticated
   * socket alone. The public board's socket belongs to the page that opened
   * it, and a signed-out visitor is exactly the case where both facts meet:
   * no token, and a live public socket that must survive.
   *
   * The compliance portal's `link` socket is the same case and is spared for
   * the same reason. This guard listed `public` alone, and that is incident
   * I1701 W6: the hook runs on every page, sees no access token, and closes a
   * socket it does not own — after which `shouldReconnect` is false and it
   * never comes back. A page-level test cannot catch it, because the hook
   * doing the closing lives above the page.
   */
  disconnectAuth() {
    const t = this._transport?.transport;
    if (t === "public" || t === "link") return;
    this.disconnect();
  }

  // ── Subscriptions ──────────────────────────────────────────────────────

  subscribe(keys) {
    const added = [];
    for (const key of this._normalise(keys)) {
      const count = this.keyCounts.get(key) || 0;
      if (count === 0) {
        if (this.keyCounts.size >= MAX_SUBSCRIPTION_KEYS) {
          console.warn(`[WS] Subscription cap of ${MAX_SUBSCRIPTION_KEYS} keys reached — dropping ${key}`);
          continue;
        }
        added.push(key);
      }
      this.keyCounts.set(key, count + 1);
    }
    if (added.length) this._send({ type: "subscribe", keys: added });
  }

  unsubscribe(keys) {
    const dropped = [];
    for (const key of this._normalise(keys)) {
      const count = this.keyCounts.get(key) || 0;
      if (count <= 1) {
        this.keyCounts.delete(key);
        if (count === 1) dropped.push(key);
      } else {
        this.keyCounts.set(key, count - 1);
      }
    }
    if (dropped.length) this._send({ type: "unsubscribe", keys: dropped });
  }

  _normalise(keys) {
    return (Array.isArray(keys) ? keys : [keys]).filter(Boolean);
  }

  _resendSubscriptions() {
    const keys = [...this.keyCounts.keys()];
    if (keys.length) this._send({ type: "subscribe", keys });
  }

  _send(frame) {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(frame));
  }

  // ── Listeners ──────────────────────────────────────────────────────────

  /** Every event envelope, plus the synthetic `__open__` and `__close__`. */
  listen(handler) {
    this.listeners.add(handler);
    return () => this.listeners.delete(handler);
  }

  _emit(envelope) {
    this.listeners.forEach((handler) => handler(envelope));
  }

  get isConnected() {
    return this.socket?.readyState === WebSocket.OPEN;
  }
}

const wsClient = new WebSocketClient();
export default wsClient;
